import { type Accessor, createEffect, onCleanup } from "solid-js";
import { useUpdateViewsCount } from "../api";

/**
 * Registra una vista per **ogni** offerta che l'istanza mostra, attribuita a
 * quella giusta.
 *
 * La vista si conta quando l'offerta **smette** di essere a schermo, e da lì
 * nasceva il difetto: `onCleanup` a livello di componente copre una sola delle
 * due uscite — lo smontaggio — mentre l'altra, l'id che cambia sotto
 * un'istanza viva, non la vede affatto. Con l'id congelato al setup una sola
 * vista partiva, e per l'offerta sbagliata: la prima, non quella letta.
 *
 * Che l'istanza attraversi più offerte non è ipotetico. Ai due mount point di
 * preview non c'è niente che smonti; nella joblist il dialog smonta **dopo**
 * l'animazione di chiusura, e in quei 200ms i click di sfondo sono già
 * sbloccati — un'altra offerta cliccata dentro quella finestra riusa l'istanza.
 * È la stessa finestra che aveva reso non reattivo l'id dell'azienda.
 *
 * Il cleanup **dell'effetto** è l'unico punto che copre entrambe le uscite:
 * Solid lo esegue sia prima di rieseguire l'effetto sia allo smontaggio. Un id
 * vuoto vale «non contare»: è così che la preview passa di qui senza un ramo
 * suo.
 */
export const useTrackJobOfferView = (jobId: Accessor<string>) => {
  const viewsCount = useUpdateViewsCount();

  createEffect(() => {
    const id = jobId();
    if (!id) return;

    onCleanup(() => viewsCount.mutate(id));
  });

  // Lo stato della mutazione non deve sopravvivere all'offerta che l'ha
  // prodotto: da qui in poi lo stesso oggetto ne serve altre.
  createEffect(() => {
    if (viewsCount.isSuccess) viewsCount.reset();
  });
};
